/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Column } from 'primereact/column';
import { PaginatorPageState } from 'primereact/paginator';
import { InputSwitch, InputSwitchChangeParams } from 'primereact/inputswitch';
import {
	DataTable,
	DataTablePFSEvent,
	DataTableSortOrderType
} from 'primereact/datatable';

import Filters from './filters/Filters';
import Pagination from '../common/pagination/Pagination';
import { LeadingBrandsListingResponseModel } from '../../models/leadingBrands/LeadingBrandsListingResponseModel';
import { LeadingBrandsSearchResponseModel } from '../../models/leadingBrands/LeadingBrandsSearchResponseModel';
import { DropdownOption } from '../../models/common/DropdownOption';
import { HttpService, IHttpService } from '../../services/httpService';
import {
	INotifierService,
	NotifierService
} from '../../services/notifierService';
import { nameof } from '../../shared/utility';
import styles from './LeadingBrands.module.scss';
import { useCookies } from 'react-cookie';

const LeadingBrands = (): JSX.Element => {
	const [loading, setLoading] = useState<boolean>(false);
	const [first, setFirst] = useState<number>(0);
	const [rows, setRows] = useState<number>(10);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [sortField, setSortField] = useState<string>('');
	const [sortOrder, setSortOrder] = useState<DataTableSortOrderType>();
	const [brandCodeFilter, setBrandCodeFilter] = useState<string>('');
	const [brandDescriptionFilter, setBrandDescriptionFilter] =
		useState<string>('');
	const [divisionFilter, setDivisionFilter] = useState<string | number>('');
	const [countryFilter, setCountryFilter] = useState<string | number>('');
	const [shouldReload, setShouldReload] = useState<boolean>(false);

	const [divisionsOptions, setDivisionsOptions] = useState<DropdownOption[]>(
		[]
	);
	const [countriesOptions, setCountriesOptions] = useState<DropdownOption[]>(
		[]
	);
	const [productCategoriesOptions, setProductCategoriesOptions] = useState<
		DropdownOption[]
	>([]);

	const [selectedProductCategories, setSelectedProductCategories] = useState<
		[]
	>([]);

	const [leadingBrandsSearch, setLeadingBrandsSearch] =
		useState<LeadingBrandsSearchResponseModel>();

	const [leadingBrandsListing, setLeadingBrandsListing] = useState<
		LeadingBrandsListingResponseModel[]
	>([]);

	const [cookies, setCookie] = useCookies([
		'leadingBrandsDivision',
		'leadingBrandsCountry',
		'leadingBrandsProductCategories'
	]);

	const httpService: IHttpService = useMemo(() => new HttpService(), []);
	const notifierService: INotifierService = useMemo(
		() => new NotifierService(),
		[]
	);

	const selectedProductCategoriesQuery = selectedProductCategories?.reduce(
		(prevCompetitor, currCompetitor, index) =>
			prevCompetitor +
			`&productCategoriesIds[${index}]=${currCompetitor}`,
		''
	);

	const fetchLeadingBrands = () => {
		setLoading(true);

		let order = '';
		if (sortOrder) {
			order = sortOrder === 1 ? 'asc' : sortOrder === -1 ? 'desc' : '';
		}
		httpService
			.get<LeadingBrandsSearchResponseModel>(
				`leadingBrands?page=${currentPage}&brandCode=${brandCodeFilter}&brandDescription=${brandDescriptionFilter}&division=${divisionFilter}&country=${countryFilter}${
					selectedProductCategoriesQuery
						? selectedProductCategoriesQuery
						: ''
				}&sortBy=${sortField}&order=${order}`,
				undefined
			)
			.then((brands) => {
				setLeadingBrandsSearch(brands);
				setLeadingBrandsListing(brands.models);
				setLoading(false);
			})
			.catch((err) => notifierService.error());
	};

	useEffect(() => {
		fetchLeadingBrands();
	}, [
		httpService,
		notifierService,
		currentPage,
		sortField,
		sortOrder,
		brandCodeFilter,
		brandDescriptionFilter,
		divisionFilter,
		countryFilter,
		selectedProductCategories
	]);

	useEffect(() => {
		if (shouldReload) {
			fetchLeadingBrands();
		}
	}, [shouldReload]);

	useEffect(() => {
		httpService
			.get<DropdownOption[]>('divisions/dropdown', undefined)
			.then((options) => {
				setDivisionsOptions(options);
				setDivisionFilter(
					cookies.leadingBrandsDivision !== undefined
						? parseInt(cookies.leadingBrandsDivision)
						: ''
				);
			})
			.catch((err) => notifierService.error());

		httpService
			.get<DropdownOption[]>('countries/dropdown', undefined)
			.then((options) => {
				setCountriesOptions(options);
				setCountryFilter(
					cookies.leadingBrandsCountry !== undefined
						? parseInt(cookies.leadingBrandsCountry)
						: ''
				);
			})
			.catch((err) => notifierService.error());

		httpService
			.get<DropdownOption[]>(
				`productDetails/categories?divisionId=${divisionFilter}`,
				undefined
			)
			.then((options) => {
				setProductCategoriesOptions(options);
				setSelectedProductCategories(
					cookies.leadingBrandsProductCategories !== undefined
						? cookies.leadingBrandsProductCategories
						: []
				);
			})
			.catch((err) => notifierService.error());
	}, [httpService, notifierService, countryFilter, divisionFilter]);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const handleInputSwitchChange = async (
		e: InputSwitchChangeParams,
		propName: keyof LeadingBrandsListingResponseModel
	) => {
		const leadingBrandId: number = +e.target.id;
		const leadingBrandIndex: number = leadingBrandsListing.findIndex(
			(b) => b.id === leadingBrandId
		);

		const brand = leadingBrandsListing[leadingBrandIndex];

		(brand as any)[propName] = e.target.value;

		try {
			await httpService.put(
				`leadingBrands/${leadingBrandId}/changeActive`,
				undefined,
				undefined,
				false
			);

			let order = '';
			if (sortOrder) {
				order =
					sortOrder === 1 ? 'asc' : sortOrder === -1 ? 'desc' : '';
			}

			const brands =
				await httpService.get<LeadingBrandsSearchResponseModel>(
					`leadingBrands?page=${currentPage}&brandCode=${brandCodeFilter}&brandDescription=${brandDescriptionFilter}&division=${divisionFilter}&country=${countryFilter}${
						selectedProductCategoriesQuery
							? selectedProductCategoriesQuery
							: ''
					}&sortBy=${sortField}&order=${order}`,
					undefined
				);

			setLeadingBrandsListing(brands.models);
		} catch {
			notifierService.error();
		}
	};

	const onRenderActive = useCallback(
		(cellData: LeadingBrandsListingResponseModel) => {
			return (
				<InputSwitch
					id={cellData.id.toString()}
					checked={cellData.active}
					onChange={(e) => handleInputSwitchChange(e, 'active')}
				/>
			);
		},
		[handleInputSwitchChange]
	);

	const onRenderDivision = (cellData: LeadingBrandsListingResponseModel) => {
		return (
			<>
				<span>
					{cellData.divisionCode} - {cellData.divisionDescription}
				</span>
			</>
		);
	};

	const onRenderCountry = (cellData: LeadingBrandsListingResponseModel) => {
		return (
			<>
				<span>
					{cellData.countryCode} - {cellData.countryDescription}
				</span>
			</>
		);
	};

	const onPageChange = (event: PaginatorPageState) => {
		setFirst(event.first);
		setRows(event.rows);
		setCurrentPage(event.page! + 1);
	};

	const onSort = (event: DataTablePFSEvent) => {
		setSortField(event.sortField);
		setSortOrder(event.sortOrder);
	};

	return (
		<>
			<Filters
				divisionsOptions={divisionsOptions}
				countriesOptions={countriesOptions}
				setBrandCodeFilter={setBrandCodeFilter}
				setBrandDescriptionFilter={setBrandDescriptionFilter}
				divisionFilter={divisionFilter}
				setDivisionFilter={setDivisionFilter}
				countryFilter={countryFilter}
				setCountryFilter={setCountryFilter}
				setShouldReload={setShouldReload}
				productCategoriesOptions={productCategoriesOptions}
				selectedProductCategories={selectedProductCategories}
				setSelectedProductCategories={setSelectedProductCategories}
			/>

			<DataTable
				value={leadingBrandsListing}
				responsiveLayout='scroll'
				sortField={sortField}
				sortOrder={sortOrder}
				onSort={onSort}
				loading={loading}
			>
				<Column
					style={{ width: '10%' }}
					sortable
					header='Status'
					body={onRenderActive}
					field={nameof<LeadingBrandsListingResponseModel>('active')}
				/>

				<Column
					sortable
					bodyClassName={styles.boldText}
					header='Brand code'
					field={nameof<LeadingBrandsListingResponseModel>(
						'brandCode'
					)}
				/>

				<Column
					sortable
					header='Brand description'
					field={nameof<LeadingBrandsListingResponseModel>(
						'brandDescription'
					)}
				/>

				<Column
					sortable
					bodyClassName={styles.boldText}
					header='Division'
					body={onRenderDivision}
					field={nameof<LeadingBrandsListingResponseModel>(
						'divisionDescription'
					)}
				/>

				<Column
					sortable
					header='Country'
					body={onRenderCountry}
					field={nameof<LeadingBrandsListingResponseModel>(
						'countryDescription'
					)}
				/>

				<Column
					sortable
					header='Product category'
					field={nameof<LeadingBrandsListingResponseModel>(
						'productDetailDescription'
					)}
				/>
			</DataTable>

			<Pagination
				totalRecords={leadingBrandsSearch?.total}
				rows={rows}
				first={first}
				onPageChange={onPageChange}
			/>
		</>
	);
};

export default LeadingBrands;
